import styled from 'styled-components'
import { MutableRefObject } from 'react'

import { Typography } from 'components/Typography/Typography'
import { IconInfo } from 'components/Icons/InfoIcon'
import { IconPlusPrice } from 'components/Icons/IconPlus'
import { IconMinusPrice } from 'components/Icons/IconMinusPrice'
import { useIsMobile } from 'utils/useIsMobile'
import { CurrentPrice } from './CurrentPrice'
import { Tooltip } from 'components/Tooltip/Tooltip'

const SetPriceRange = styled(Typography)`
  display: flex;
  align-items: center;
  // margin: 16px 0;
  margin-top: 32px;
  margin-bottom: 12px;
  margin-left: 0;
  margin-right: 0;
  & > div {
    display: flex;
    cursor: pointer;
    & > svg {
      margin-left: 6px;
      path {
        stroke: ${p => p.theme.colors.text.secondary};
      }
    }
  }
`

const Container = styled.div`
  display: flex;
  gap: 16px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    flex-direction: column;
    gap: 0;
  }
`

const ContainerItem = styled.div<{ disabled?: boolean; $error?: boolean }>`
  position: relative;
  padding: 16px;
  border-radius: 8px;
  border: 0.7px solid ${p => (p.$error ? p.theme.colors.text.danger : p.theme.colors.divider)};
  ${p => p.disabled && `background-color: ${p.theme.colors.background.secondary}`};

  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    width: 50%;
  }
`

const ContainerItemLabel = styled.div`
  display: flex;
  width: 50%;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    width: 100%;
  }
`

const PriceInput = styled.input<{ ref: MutableRefObject<HTMLElement | null> }>`
  border: none;
  background-color: transparent;
  font-size: ${p => p.theme.typography.size.Label1}px;
  font-weight: ${p => p.theme.typography.weight.Bold};
  font-family: ${p => p.theme.typography.family.Poppins};
  color: ${p => p.theme.colors.text.primary};
  ${p => p.disabled && `background-color: ${p.theme.colors.background.secondary}`};
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    font-size: ${p => p.theme.typography.size.Description}px;
  }
`

const PriceIconContainer = styled.div<{ disabled: boolean }>`
  cursor: ${p => (p.disabled ? 'auto' : 'pointer')};
  ${p => p.disabled && `pointer-events: none`};
  display: flex;
`

interface Props {
  currentPrice?: string
  tokenXSymbol: string
  tokenYSymbol: string
  tickAError: boolean
  tickBError: boolean
  disabled: boolean
  rangeMinRef: MutableRefObject<HTMLElement | null>
  rangeMaxRef: MutableRefObject<HTMLElement | null>
  // placeholder?: string
  rangeMin: string
  rangeMax: string
  handleRangeMinChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleRangeMaxChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleMinPricePlusClick: () => void
  handleMinPriceMinusClick: () => void
  handleMaxPricePlusClick: () => void
  handleMaxPriceMinusClick: () => void
}

export const PriceRange = ({
  currentPrice,
  tokenXSymbol,
  tokenYSymbol,
  tickAError,
  tickBError,
  disabled,
  rangeMinRef,
  rangeMaxRef,
  rangeMin,
  rangeMax,
  handleRangeMinChange,
  handleRangeMaxChange,
  handleMinPriceMinusClick,
  handleMinPricePlusClick,
  handleMaxPriceMinusClick,
  handleMaxPricePlusClick,
}: Props) => {
  const isMobile = useIsMobile()

  return (
    <div>
      <SetPriceRange variant="label2" color="secondary" fontWeight="medium">
        Set Price Range
        <Tooltip content="Define the price range in which your liquidity will be active and earn fees." position="top">
          <IconInfo width={16} height={16} style={{ marginLeft: 6 }} />
        </Tooltip>
      </SetPriceRange>
      <CurrentPrice tokenXSymbol={tokenXSymbol} tokenYSymbol={tokenYSymbol} currentPrice={currentPrice} />
      {!isMobile && (
        <Container>
          <ContainerItemLabel>
            <Typography variant="body" color="secondary">
              Min Price
            </Typography>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <PriceIconContainer onClick={handleMinPriceMinusClick} disabled={disabled} style={{ marginRight: 16 }}>
                <IconMinusPrice />
              </PriceIconContainer>
              <PriceIconContainer onClick={handleMinPricePlusClick} disabled={disabled}>
                <IconPlusPrice />
              </PriceIconContainer>
            </div>
          </ContainerItemLabel>
          <ContainerItemLabel>
            <Typography variant="body" color="secondary">
              Max Price
            </Typography>
            <div style={{ display: 'flex' }}>
              <PriceIconContainer onClick={handleMaxPriceMinusClick} disabled={disabled} style={{ marginRight: 16 }}>
                <IconMinusPrice />
              </PriceIconContainer>
              <PriceIconContainer onClick={handleMaxPricePlusClick} disabled={disabled}>
                <IconPlusPrice />
              </PriceIconContainer>
            </div>
          </ContainerItemLabel>
        </Container>
      )}
      <Container>
        {isMobile && (
          <ContainerItemLabel>
            <Typography variant="body" color="secondary">
              Min Price
            </Typography>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <PriceIconContainer onClick={handleMinPriceMinusClick} disabled={disabled} style={{ marginRight: 16 }}>
                <IconMinusPrice />
              </PriceIconContainer>
              <PriceIconContainer onClick={handleMinPricePlusClick} disabled={disabled}>
                <IconPlusPrice />
              </PriceIconContainer>
            </div>
          </ContainerItemLabel>
        )}
        <>
          <ContainerItem disabled={disabled} $error={tickAError}>
            <Typography variant={isMobile ? 'description' : 'label1'} fontWeight="bold">
              <PriceInput value={rangeMin} ref={rangeMinRef} onChange={handleRangeMinChange} disabled={disabled} />
            </Typography>
            <Typography variant="caption" color="secondary">
              {tokenYSymbol} per {tokenXSymbol}
            </Typography>
            {tickAError && (
              <div style={{ position: 'absolute', bottom: -24, left: 0 }}>
                <Typography variant="caption" color="danger">
                  Min price too high. Position must be in range.
                </Typography>
              </div>
            )}
          </ContainerItem>
        </>
        {isMobile && (
          <ContainerItemLabel style={{ marginTop: 32 }}>
            <Typography variant="body" color="secondary">
              Max Price
            </Typography>
            <div style={{ display: 'flex' }}>
              <PriceIconContainer onClick={handleMaxPriceMinusClick} disabled={disabled} style={{ marginRight: 16 }}>
                <IconMinusPrice />
              </PriceIconContainer>
              <PriceIconContainer onClick={handleMaxPricePlusClick} onChange={handleRangeMaxChange} disabled={disabled}>
                <IconPlusPrice />
              </PriceIconContainer>
            </div>
          </ContainerItemLabel>
        )}
        <ContainerItem disabled={disabled} $error={tickBError}>
          <PriceInput value={rangeMax} ref={rangeMaxRef} onChange={handleRangeMaxChange} disabled={disabled} />
          <Typography variant="caption" color="secondary">
            {tokenYSymbol} per {tokenXSymbol}
          </Typography>
          {tickBError && (
            <div style={{ position: 'absolute', bottom: -24, left: 0 }}>
              <Typography variant="caption" color="danger">
                Max price too low. Position must be in range.
              </Typography>
            </div>
          )}
        </ContainerItem>
      </Container>
    </div>
  )
}
